/**
 * Pure rules behind the asset-page indicator dropdown — kept free of firebase
 * so they stay unit-testable in isolation (same split as
 * portfolioManagerSignal.ts).
 */

import type { SeriesConfig } from '../../../data/seriesConfig';
import type { SeriesPerformanceDoc } from '../../../data/seriesPerformance';
import { filterSeriesByTicker } from '../../../data/seriesUtils';
import {
    INDICATOR_DISPLAY_NAMES,
    KNOWN_FRACTIONAL_SERIES,
    portfolioManagerInPosition,
} from './portfolioManagerSignal';

/**
 * A series is offered under an asset once it held that asset on this share of
 * its invested days. Keeps out series that touched an asset once, keeps in
 * genuine multi-asset strategies — `clustering:agg_4_btc|…` holds ETH on ~40%
 * and BTC on ~31% of its invested days and belongs under both.
 *
 * Mirrors ASSET_SHARE_THRESHOLD in the indexer's series_performance.py.
 */
export const ASSET_SHARE_THRESHOLD = 0.1;

export interface IndicatorOption {
    series: string;
    /** Human-readable name from the catalog. */
    name: string;
    ret30d: number | null;
    ret365d: number | null;
    sharpe365d: number | null;
    fractional: boolean;
    /** The default series, pinned to the top of the list whatever the sort. */
    isDefault: boolean;
}

/**
 * The numeric columns, in the order they are shown — both as sort toggles in
 * the menu header and as values on every row. One list drives both, so the
 * header and the rows can never disagree about the order, which is what makes
 * a sort look broken when it is not.
 */
export const METRIC_KEYS = ['ret365d', 'ret30d', 'sharpe365d'] as const;
export type IndicatorMetricKey = (typeof METRIC_KEYS)[number];

export const SORT_KEYS = [...METRIC_KEYS, 'name'] as const;
export type IndicatorSortKey = (typeof SORT_KEYS)[number];

/** Short captions, used identically in the header and on each row. */
export const SORT_LABELS: Record<IndicatorSortKey, string> = {
    ret365d: '1Y',
    ret30d: '30D',
    sharpe365d: 'SR',
    name: 'Name',
};

/**
 * Order options by `sortKey`, with the default always first.
 *
 * Unmeasured options sort last rather than being treated as a 0 — a series too
 * young to have a figure is not the same as one that returned nothing.
 */
export function sortIndicatorOptions(
    options: IndicatorOption[],
    sortKey: IndicatorSortKey
): IndicatorOption[] {
    const rest = options.filter(o => !o.isDefault);
    const pinned = options.filter(o => o.isDefault);

    rest.sort((a, b) => {
        if (sortKey === 'name') return a.name.localeCompare(b.name);
        const x = a[sortKey];
        const y = b[sortKey];
        if (x === null && y === null) return a.name.localeCompare(b.name);
        if (x === null) return 1;
        if (y === null) return -1;
        return y - x;
    });

    return [...pinned, ...rest];
}

/**
 * Options for one asset, best 1-year return first.
 *
 * Filters on the measured asset shares rather than a name substring: the
 * series named `rotation:ethereum:*` are named after a config file, not the
 * asset they trade, and the best "BTC" series actually holds mostly ETH.
 */
export function buildIndicatorOptions(
    catalog: SeriesConfig[],
    perf: SeriesPerformanceDoc | null,
    assetKey: 'btc' | 'eth',
    defaultSeries: string
): IndicatorOption[] {
    // No document yet (the indexer job has never run): fall back to the
    // name-based filter the Strategy tab uses. It is the weaker rule — it lets
    // rotation:ethereum:* through and misses series that trade an asset
    // without naming it — but a full list with blank figures beats a dropdown
    // holding a single option.
    const measured = perf?.series ?? null;
    const candidates =
        measured === null ? filterSeriesByTicker(catalog, assetKey) : catalog;

    const options: IndicatorOption[] = [];

    for (const config of candidates) {
        const entry = measured?.[config.series];
        const isDefault = config.series === defaultSeries;
        if (measured !== null && !isDefault) {
            // Only signals a live sub-account actually trades. Showing a series
            // nobody trades invites a user to follow something the fund does
            // not stand behind — and buries the handful that matter under
            // dozens that do not.
            if (!entry?.deployed) continue;
            const share = entry.assets?.[assetKey] ?? 0;
            if (share < ASSET_SHARE_THRESHOLD) continue;
        }
        options.push({
            series: config.series,
            name: INDICATOR_DISPLAY_NAMES[config.series] ?? config.name,
            ret30d: entry?.ret_30d ?? null,
            ret365d: entry?.ret_365d ?? null,
            sharpe365d: entry?.sharpe_365d ?? null,
            fractional:
                entry?.fractional ?? KNOWN_FRACTIONAL_SERIES.has(config.series),
            isDefault,
        });
    }

    // The default must be selectable even if the name filter did not match it.
    if (!options.some(o => o.series === defaultSeries)) {
        const config = catalog.find(c => c.series === defaultSeries);
        if (config) {
            const entry = measured?.[config.series];
            options.push({
                series: config.series,
                name: INDICATOR_DISPLAY_NAMES[config.series] ?? config.name,
                ret30d: entry?.ret_30d ?? null,
                ret365d: entry?.ret_365d ?? null,
                sharpe365d: entry?.sharpe_365d ?? null,
                fractional: KNOWN_FRACTIONAL_SERIES.has(config.series),
                isDefault: true,
            });
        }
    }

    return sortIndicatorOptions(options, 'ret365d');
}

/**
 * Which series to show: the user's stored choice when it is still on offer,
 * otherwise the default.
 *
 * An empty `options` means "not loaded yet", not "nothing is available" —
 * falling back to the default there would flash the wrong series on every
 * page load before the catalog arrives.
 */
export function resolveSelection(
    stored: string | null | undefined,
    options: IndicatorOption[],
    defaultSeries: string
): string {
    if (!stored) return defaultSeries;
    if (options.length === 0) return stored;
    return options.some(o => o.series === stored) ? stored : defaultSeries;
}

/** The displayed text for one metric cell — "—" when unmeasured, never a 0. */
export function formatMetric(
    option: IndicatorOption,
    key: IndicatorMetricKey
): string {
    const value = option[key];
    if (value === null) return '—';
    if (key === 'sharpe365d') return value.toFixed(2);
    const pct = value * 100;
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

/**
 * Value driving the green/red band on the price chart.
 *
 * A fractional series is a continuous dial on one asset, so its own weight is
 * the value. A binary series is an in/out switch that may hold any asset, so
 * the shared in-position rule decides.
 */
export function indicatorValue(
    data: any,
    fractional: boolean,
    assetKey: 'btc' | 'eth'
): number {
    if (!fractional) return portfolioManagerInPosition(data);
    const raw = Number(data?.target_distribution?.[assetKey]);
    return Number.isFinite(raw) ? raw : 0;
}
